import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { AlertTriangle, CalendarIcon } from 'lucide-react'
import { FC, useMemo, useState } from 'react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { BillingFormData } from '@/containers/logistic/purchaseOrders/wizard/NewPurchaseOrderWizard'
import { useBusinessCommercialsQuery } from '@/hooks/business/useBusinessCommercialsQuery'
import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { cn } from '@/lib/utils'

interface DocumentDetailsStepProps {
  formData: BillingFormData
  onNext: (data: Partial<BillingFormData>) => void
  setIsMainButtonDisabled: (disabled: boolean) => void
}

const DocumentDetailsStep: FC<DocumentDetailsStepProps> = ({
  formData,
  onNext,
}) => {
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  // Get commercial personnel using the new hook
  const {
    businessCommercials,
    isLoadingBusinessCommercials,
    isErrorBusinessCommercials,
    errorBusinessCommercials,
  } = useBusinessCommercialsQuery({
    businessId: businessData?.id ?? 0,
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })

  const [title, setTitle] = useState(formData.title || '')
  const [commercialId, setCommercialId] = useState(
    formData.commercialId || undefined
  )
  const [paymentTerm, setPaymentTerm] = useState(
    formData.paymentTerm || 'Inmediato'
  )
  const [customDueDate, setCustomDueDate] = useState<Date | undefined>(
    formData.customDueDate
  )
  const [observations, setObservations] = useState(formData.observations || '')

  // Check if commercial was already assigned by client selection in step 1
  const clientHasCommercial = useMemo(() => {
    return !!formData.commercialId && !formData.isNewCommercial
  }, [formData.commercialId, formData.isNewCommercial])

  // Transform commercial personnel into select options
  const commercialOptions = useMemo(() => {
    if (!businessCommercials || businessCommercials.length === 0) {
      return []
    }

    return businessCommercials.map((commercial) => ({
      value: commercial.id.toString(),
      label: commercial.displayName || commercial.username,
    }))
  }, [businessCommercials])

  // Get the selected commercial's name for display
  const selectedCommercialName = useMemo(() => {
    if (!commercialId) return null
    const commercial = commercialOptions.find((c) => c.value === commercialId)
    return commercial?.label || null
  }, [commercialId, commercialOptions])

  const calculatedDueDate = useMemo(() => {
    if (
      paymentTerm === 'Personalizado' ||
      !paymentTerm ||
      paymentTerm === 'Inmediato'
    )
      return null
    const days = parseInt(paymentTerm.replace(' días', ''))
    if (isNaN(days)) return null
    const date = new Date()
    date.setDate(date.getDate() + days)
    return date
  }, [paymentTerm])

  // Handle input changes with error clearing
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
  }

  const handleCommercialChange = (value: string) => {
    setCommercialId(value)
  }

  const handleNextClick = () => {
    onNext({
      title,
      commercialId,
      paymentTerm,
      customDueDate:
        paymentTerm === 'Personalizado'
          ? customDueDate
          : calculatedDueDate || undefined,
      observations,
    })
  }

  // Error display component
  const ErrorText: FC<{ error?: string; className?: string }> = ({
    error,
    className,
  }) => {
    if (!error) return null
    return <p className={cn('text-sm text-red-500', className)}>{error}</p>
  }

  // Loading state
  if (isLoadingBusinessCommercials) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Detalles de la Orden de Compra</h3>
        <Alert>
          <AlertDescription>
            Cargando información de comerciales...
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  // Error state
  if (isErrorBusinessCommercials) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Detalles de la Orden de Compra</h3>
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error cargando comerciales: {errorBusinessCommercials?.message}
            <br />
            Por favor, intenta recargar la página.
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Detalles de la Orden de Compra</h3>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="title">Título</Label>
          <Input
            id="title"
            value={title}
            onChange={handleTitleChange}
            placeholder="Ej: Orden de Compra Enero"
          />
        </div>

        <div className="space-y-1">
          <Label htmlFor="commercial">Comercial que atiende</Label>
          <Select
            value={commercialId}
            onValueChange={handleCommercialChange}
            disabled={clientHasCommercial} // Disable if client has commercial assigned
          >
            <SelectTrigger id="commercial">
              <SelectValue
                placeholder={
                  commercialOptions.length === 0
                    ? 'No hay comerciales disponibles'
                    : clientHasCommercial && selectedCommercialName
                      ? selectedCommercialName
                      : clientHasCommercial
                        ? 'Comercial asignado por proveedor'
                        : 'Seleccionar comercial'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {commercialOptions.length > 0 ? (
                commercialOptions.map((commercial) => (
                  <SelectItem
                    key={commercial.value}
                    value={commercial.value}
                    className="cursor-pointer"
                  >
                    {commercial.label}
                  </SelectItem>
                ))
              ) : (
                <SelectItem value="no-commercials" disabled>
                  No hay comerciales disponibles
                </SelectItem>
              )}
            </SelectContent>
          </Select>

          {/* Helper text for disabled state */}
          {clientHasCommercial && (
            <p className="text-xs text-muted-foreground">
              Este comercial está asignado automáticamente por el proveedor
              seleccionado
            </p>
          )}

          {/* Empty state helper */}
          {!clientHasCommercial && commercialOptions.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No hay comerciales activos disponibles en el sistema
            </p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="paymentTerm">Plazo de pago</Label>
          <Select value={paymentTerm} onValueChange={setPaymentTerm}>
            <SelectTrigger id="paymentTerm">
              <SelectValue placeholder="Seleccionar plazo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Inmediato" className="cursor-pointer">
                Pago inmediato
              </SelectItem>
              <SelectItem value="7 días" className="cursor-pointer">
                7 días
              </SelectItem>
              <SelectItem value="15 días" className="cursor-pointer">
                15 días
              </SelectItem>
              <SelectItem value="30 días" className="cursor-pointer">
                30 días
              </SelectItem>
              <SelectItem value="Personalizado" className="cursor-pointer">
                Personalizado
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {paymentTerm === 'Personalizado' && (
          <div className="space-y-1">
            <Label htmlFor="customDueDate">Fecha de Vencimiento</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="customDueDate"
                  variant={'outline'}
                  className={'w-full justify-start text-left font-normal'}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {customDueDate ? (
                    format(customDueDate, 'PPP', { locale: es })
                  ) : (
                    <span>Seleccionar fecha</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={customDueDate}
                  onSelect={setCustomDueDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {calculatedDueDate && paymentTerm !== 'Personalizado' && (
          <div className="-mt-2 space-y-1 pt-0 md:mt-0 md:pt-9">
            <p className="text-sm text-muted-foreground">
              Vence el: {format(calculatedDueDate, 'PPP', { locale: es })}
            </p>
          </div>
        )}

        <div className="space-y-1 md:col-span-2">
          <Label htmlFor="observations">Observaciones</Label>
          <Textarea
            id="observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            placeholder="Añadir notas u observaciones..."
          />
        </div>
      </div>

      <button
        onClick={handleNextClick}
        className="hidden"
        id="document-details-next"
        disabled={isLoadingBusinessCommercials}
      ></button>
    </div>
  )
}

export default DocumentDetailsStep
